import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";

import { platformStatsQuery } from "./platformStatsQuery";

/** Lets the server's clock pass the boundary before asking for the next one. */
const BOUNDARY_GRACE_MS = 2_000;
/** Longest delay `setTimeout` holds before it fires immediately. */
const MAX_TIMEOUT_MS = 2 ** 31 - 1;

export interface NextDistribution {
    /** ISO time the Program next pays. Null until loaded, and when
     *  the Program is not Active or has ended. */
    at: string | null;
    /** ISO start of the period that pays at `at`. Null whenever `at` is. */
    periodStartsAt: string | null;
}

/**
 * The period the main-app Program is paying on, from GET /api/yaas/stats.
 * yaas-api computes it with the same function as the issuer console's
 * Next Payout, so the two always agree. Refetches as the countdown
 * reaches zero so it rolls to the following period at once instead of
 * waiting for the five-minute poll.
 */
export function useNextDistribution(): NextDistribution {
    const { data, refetch } = useQuery(platformStatsQuery);
    const at = data?.distributionSchedule?.nextPayoutAt ?? null;
    const periodStartsAt = data?.distributionSchedule?.currentPeriodStartsAt ?? null;

    useEffect(() => {
        if (at === null) return;
        const delay = Math.max(0, Date.parse(at) - Date.now()) + BOUNDARY_GRACE_MS;
        if (!Number.isFinite(delay) || delay > MAX_TIMEOUT_MS) return;
        const timer = setTimeout(() => void refetch(), delay);
        return (): void => clearTimeout(timer);
    }, [at, refetch]);

    return useMemo(() => ({ at, periodStartsAt }), [at, periodStartsAt]);
}
